import type { CogniteClient } from '@cognite/sdk';
import OpenAI, { type ClientOptions } from 'openai';
import type * as Core from 'openai/core';

import { cogniteAIApiFetch } from './cogniteAIApiFetch';

/** API Client for interfacing with the Azure OpenAI API. */
export interface CogniteAtlasClientOptions extends ClientOptions {
  cogniteClient: CogniteClient;
}
/** API Client for interfacing with the OpenAI API. */
export class CogniteAtlasAPIClient extends OpenAI {
  private _clientOptions: ClientOptions;
  private _cogniteClient: CogniteClient;

  constructor({ cogniteClient, ...options }: CogniteAtlasClientOptions) {
    super(options);
    this._clientOptions = options;
    this._cogniteClient = cogniteClient;
  }

  static getDefaultClientOptions(cogniteClient: CogniteClient) {
    const apiOrigin = cogniteClient.getBaseUrl();
    const baseURL = `${apiOrigin}/api/v1/projects/${cogniteClient.project}/ai`;
    const clientOptions: CogniteAtlasClientOptions = {
      cogniteClient,
      apiKey: '', // we are using custom http client, so ignoring this
      baseURL,
      // Give a hint to openai client to use browser apis
      // security is not an issue since we are using custom fetch and our authentication
      dangerouslyAllowBrowser: true,
      fetch: cogniteAIApiFetch,
      maxRetries: 2,
    };

    return clientOptions;
  }

  protected override defaultHeaders(_opts: Core.FinalRequestOptions): Core.Headers {
    const cogniteApiHeaders = {
      ...this._cogniteClient.getDefaultRequestHeaders(),
      accept: 'text/event-stream',
      'content-type': 'application/json',
      'cdf-version': 'alpha',
    };
    return {
      ...super.defaultHeaders(_opts),
      ...this._clientOptions.defaultHeaders,
      ...cogniteApiHeaders,
    };
  }

  protected override authHeaders(_opts: Core.FinalRequestOptions): Core.Headers {
    const headers = this._cogniteClient.getDefaultRequestHeaders();
    const token = headers.Authorization;
    return { Authorization: token };
  }
}
